import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBillingV1ManualTaxMappingServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/manual-tax-mappings',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/manual-tax-mappings',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/billing/v1/manual-tax-mappings',
        destPath: '/v1/manual-tax-mappings',
      },
      {
        srcPath: '/billing/v1/bulk/manual-tax-mappings',
        destPath: '/v1/bulk/manual-tax-mappings',
      },
    ],
    _: [
      {
        srcPath: '/billing/v1/manual-tax-mappings',
        destPath: '/v1/manual-tax-mappings',
      },
      {
        srcPath: '/billing/v1/bulk/manual-tax-mappings',
        destPath: '/v1/bulk/manual-tax-mappings',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/billing/v1/manual-tax-mappings',
        destPath: '/v1/manual-tax-mappings',
      },
      {
        srcPath: '/billing/v1/bulk/manual-tax-mappings',
        destPath: '/v1/bulk/manual-tax-mappings',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_manual-tax-mappings';

/**
 * Creates a manual tax mapping for a specific tax group and region combination.
 *
 * You must specify the tax group ID and tax region ID. The tax group and tax region
 * must already exist before creating the mapping.
 * To create multiple manual tax mappings in a single request, use [Bulk Create Manual Tax Mappings](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/manual-tax-mappings/bulk-create-manual-tax-mappings).
 */
export function createManualTaxMapping(
  payload: object
): RequestOptionsFactory<any> {
  function __createManualTaxMapping({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'manualTaxMapping.createdDate' },
          { path: 'manualTaxMapping.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.billing.v1.manual_tax_mapping',
      method: 'POST' as any,
      methodFqn:
        'wix.billing.v1.ManualTaxMappingService.CreateManualTaxMapping',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1ManualTaxMappingServiceUrl({
        protoPath: '/v1/manual-tax-mappings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'manualTaxMapping.createdDate' },
              { path: 'manualTaxMapping.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createManualTaxMapping;
}

/**
 * Creates up to 100 manual tax mappings in a single request.
 *
 * Use this method when you need to set up tax mappings for multiple tax group and region combinations at once,
 * such as when initially configuring tax rates for a new business or importing mappings from an external system.
 */
export function bulkCreateManualTaxMappings(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateManualTaxMappings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'manualTaxMappings.createdDate' },
          { path: 'manualTaxMappings.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.billing.v1.manual_tax_mapping',
      method: 'POST' as any,
      methodFqn:
        'wix.billing.v1.ManualTaxMappingService.BulkCreateManualTaxMappings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1ManualTaxMappingServiceUrl({
        protoPath: '/v1/bulk/manual-tax-mappings/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateManualTaxMappings;
}

/**
 * Retrieves a manual tax mapping by ID.
 *
 * Use this method to get details about a specific manual tax mapping, including its tax rate,
 * jurisdiction information, and associated tax group and region.
 */
export function getManualTaxMapping(
  payload: object
): RequestOptionsFactory<any> {
  function __getManualTaxMapping({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billing.v1.manual_tax_mapping',
      method: 'GET' as any,
      methodFqn: 'wix.billing.v1.ManualTaxMappingService.GetManualTaxMapping',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1ManualTaxMappingServiceUrl({
        protoPath: '/v1/manual-tax-mappings/{manualTaxMappingId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'manualTaxMapping.createdDate' },
              { path: 'manualTaxMapping.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getManualTaxMapping;
}

/**
 * Updates an existing manual tax mapping.
 *
 * Use this method to modify the tax rate or other details of an existing manual tax mapping.
 * Each time the manual tax mapping is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the manual tax mapping.
 * This ensures you're working with the latest manual tax mapping and prevents unintended overwrites.
 */
export function updateManualTaxMapping(
  payload: object
): RequestOptionsFactory<any> {
  function __updateManualTaxMapping({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'manualTaxMapping.createdDate' },
          { path: 'manualTaxMapping.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.billing.v1.manual_tax_mapping',
      method: 'PATCH' as any,
      methodFqn:
        'wix.billing.v1.ManualTaxMappingService.UpdateManualTaxMapping',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1ManualTaxMappingServiceUrl({
        protoPath: '/v1/manual-tax-mappings/{manualTaxMapping.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'manualTaxMapping.createdDate' },
              { path: 'manualTaxMapping.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateManualTaxMapping;
}

/**
 * Deletes a manual tax mapping.
 *
 * This permanently removes the custom tax rate mapping.
 * After deletion, no tax will be calculated for the associated tax group and region combination.
 */
export function deleteManualTaxMapping(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteManualTaxMapping({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billing.v1.manual_tax_mapping',
      method: 'DELETE' as any,
      methodFqn:
        'wix.billing.v1.ManualTaxMappingService.DeleteManualTaxMapping',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1ManualTaxMappingServiceUrl({
        protoPath: '/v1/manual-tax-mappings/{manualTaxMappingId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteManualTaxMapping;
}

/**
 * Retrieves a list of manual tax mappings based on the provided query criteria.
 *
 * Use this method to find manual tax mappings by tax group, tax region, or other filter criteria.
 * You can also sort and paginate the results. This is useful for managing large numbers of tax mappings
 * or finding mappings that apply to specific regions or tax groups.
 *
 * To learn how to query manual tax mappings, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
 */
export function queryManualTaxMappings(
  payload: object
): RequestOptionsFactory<any> {
  function __queryManualTaxMappings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billing.v1.manual_tax_mapping',
      method: 'GET' as any,
      methodFqn:
        'wix.billing.v1.ManualTaxMappingService.QueryManualTaxMappings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1ManualTaxMappingServiceUrl({
        protoPath: '/v1/manual-tax-mappings/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'manualTaxMappings.createdDate' },
              { path: 'manualTaxMappings.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixBillingV1ManualTaxMappingServiceUrl({
            protoPath: '/v1/manual-tax-mappings/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryManualTaxMappings;
}

/**
 * Updates extended fields of a manual tax mapping.
 *
 * Use this method to modify extended field data associated with a manual tax mapping.
 * Unlike the standard update operation, this does not increment the revision number.
 */
export function updateExtendedFields(
  payload: object
): RequestOptionsFactory<any> {
  function __updateExtendedFields({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billing.v1.manual_tax_mapping',
      method: 'POST' as any,
      methodFqn: 'wix.billing.v1.ManualTaxMappingService.UpdateExtendedFields',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1ManualTaxMappingServiceUrl({
        protoPath: '/v1/manual-tax-mappings/{id}/update-extended-fields',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'manualTaxMapping.createdDate' },
              { path: 'manualTaxMapping.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateExtendedFields;
}

/** Assigns tax exempt overrides to multiple manual tax mappings in a single request. */
export function bulkAssignExemptOverrides(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkAssignExemptOverrides({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billing.v1.manual_tax_mapping',
      method: 'POST' as any,
      methodFqn:
        'wix.billing.v1.ManualTaxMappingService.BulkAssignExemptOverrides',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1ManualTaxMappingServiceUrl({
        protoPath: '/v1/manual-tax-mappings/bulk-assign-exempt-overrides',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkAssignExemptOverrides;
}

/** Unassigns tax exempt overrides to multiple manual tax mappings in a single request. */
export function bulkUnassignExemptOverrides(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUnassignExemptOverrides({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billing.v1.manual_tax_mapping',
      method: 'POST' as any,
      methodFqn:
        'wix.billing.v1.ManualTaxMappingService.BulkUnassignExemptOverrides',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1ManualTaxMappingServiceUrl({
        protoPath: '/v1/manual-tax-mappings/bulk-unassign-exempt-overrides',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUnassignExemptOverrides;
}

/**
 * Retrieves up to 1000 manual tax mappings.
 * The returned manual tax mappings are sorted by the `createdDate` field in ascending order.
 */
export function listManualTaxMappings(
  payload: object
): RequestOptionsFactory<any> {
  function __listManualTaxMappings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billing.v1.manual_tax_mapping',
      method: 'GET' as any,
      methodFqn: 'wix.billing.v1.ManualTaxMappingService.ListManualTaxMappings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1ManualTaxMappingServiceUrl({
        protoPath: '/v1/manual-tax-mappings',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'manualTaxMappings.createdDate' },
              { path: 'manualTaxMappings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listManualTaxMappings;
}
